type FetchOptions = {
  headers?: Record<string, string>;
  signal?: AbortSignal;
  cache?: RequestCache;
  redirectOnUnauthorized?: boolean;
  returnPath?: string;
};

async function errorMessage(response: Response) {
  const body = await response.json().catch(() => null);
  const record = body && typeof body === 'object' ? (body as Record<string, unknown>) : null;
  const error = typeof record?.error === 'string' ? record.error : '';
  const message = typeof record?.message === 'string' ? record.message : '';
  return `${response.status} - ${error || message || 'Internal server error'}`;
}

/** The reply's JSON. After a 401 the browser is already going to login, so the promise never settles. */
async function readJson<T>(response: Response | null, func?: (result: T) => void): Promise<T> {
  if (!response) return new Promise<never>(() => {});
  const result: T = await response.json();
  func && func(result);
  return result;
}

/** Shared client request. A 401 sends the browser to login unless that redirect is turned off. */
export async function request(url: string, init: RequestInit = {}, options: FetchOptions = {}) {
  const response = await fetch(url, {
    ...init,
    headers: options.headers ? { ...(init.headers as Record<string, string> | undefined), ...options.headers } : init.headers,
    signal: options.signal ?? init.signal,
    cache: options.cache ?? init.cache,
  });

  if (response.status === 401 && options.redirectOnUnauthorized !== false) {
    const returnUrl = options.returnPath ? `?returnUrl=/${options.returnPath}` : '';
    window.location.href = `/login${returnUrl}`;
    return null;
  }

  if (!response.ok) throw new Error(await errorMessage(response));
  if (init.method && init.method !== 'GET') invalidateCached(url);
  return response;
}

// Small lists that many pages load (dials, locations, users…). The tab keeps one copy per URL:
// readers get the saved copy at once, and it is fetched again in the background once it is
// older than CACHED_FRESH_MS. A successful write to the same API path clears it.
const CACHED_FRESH_MS = 5 * 60_000;

type CachedEntry = { data?: unknown; fetchedAt: number; request?: Promise<unknown>; listeners: Set<() => void> };
const cachedEntries = new Map<string, CachedEntry>();

function cachedEntry(url: string) {
  let entry = cachedEntries.get(url);
  if (!entry) {
    entry = { fetchedAt: 0, listeners: new Set() };
    cachedEntries.set(url, entry);
  }
  return entry;
}

export function readCached<T>(url: string) {
  return cachedEntries.get(url)?.data as T | undefined;
}

function fetchCached(url: string) {
  const entry = cachedEntry(url);
  entry.request ??= get(url)
    .then((data) => {
      const changed = JSON.stringify(data) !== JSON.stringify(entry.data);
      entry.data = data;
      entry.fetchedAt = Date.now();
      if (changed) entry.listeners.forEach((listener) => listener());
      return data;
    })
    .finally(() => {
      entry.request = undefined;
    });
  return entry.request;
}

export function refreshCached(url: string) {
  if (Date.now() - cachedEntry(url).fetchedAt < CACHED_FRESH_MS) return;
  fetchCached(url).catch(() => {});
}

/** The saved copy when it is fresh, otherwise a new fetch. Throws like `get` when the fetch fails. */
export async function getCached<T = any>(url: string): Promise<T> {
  const entry = cachedEntry(url);
  if (entry.data !== undefined && Date.now() - entry.fetchedAt < CACHED_FRESH_MS) return entry.data as T;
  return (await fetchCached(url)) as T;
}

export function subscribeCached(url: string, listener: () => void) {
  const entry = cachedEntry(url);
  entry.listeners.add(listener);
  return () => {
    entry.listeners.delete(listener);
  };
}

/** A write to /api/settings/dial/123 clears /api/settings/dial and /api/settings/dial?…, not /api/settings/dialsetting. */
function invalidateCached(url: string) {
  const path = url.split('?')[0];
  cachedEntries.forEach((entry, cachedUrl) => {
    const cachedPath = cachedUrl.split('?')[0];
    if (path !== cachedPath && !path.startsWith(`${cachedPath}/`)) return;
    entry.fetchedAt = 0;
    if (entry.listeners.size) refreshCached(cachedUrl);
    else entry.data = undefined;
  });
}

// `T` defaults to `any` until the pages type their responses; then it becomes `unknown`.
export async function get<T = any>(url: string, func?: (result: T) => void, context?: string, signal?: AbortSignal) {
  return readJson<T>(await request(url, { signal }, { returnPath: context }), func);
}

export async function blob(url: string, func?: (result: Blob) => void) {
  const response = await request(url);
  if (!response) return;
  const result = await response.blob();
  func && func(result);
  return result;
}

export async function form<T = any>(url: string, data: FormData, func?: (result: T) => void) {
  return readJson<T>(await request(url, { method: 'POST', body: data }), func);
}

export async function formPut<T = any>(url: string, data: FormData, func?: (result: T) => void) {
  return readJson<T>(await request(url, { method: 'PUT', body: data }), func);
}

export async function put<T = any>(url: string, data: unknown, func?: (result: T) => void) {
  return readJson<T>(
    await request(url, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }),
    func
  );
}

export async function patch<T = any>(url: string, data: unknown, func?: (result: T) => void) {
  return readJson<T>(
    await request(url, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }),
    func
  );
}

export async function post<T = any>(url: string, data: unknown, func?: (result: T) => void, options?: FetchOptions) {
  return readJson<T>(
    await request(
      url,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...options?.headers },
        body: JSON.stringify(data),
        signal: options?.signal,
      },
      options
    ),
    func
  );
}

export async function del<T = any>(url: string, func?: (result: T) => void) {
  return readJson<T>(
    await request(url, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
    }),
    func
  );
}
